import { createSelector } from "reselect";
import { getPolygonCentroid } from "../../Components/Common/SharedActions";
import { MapStyleEnum } from "../../Enums/GPSMapEnum";
import { IBrandCategory } from "../../Models/IBrandCategory";
import { IBuxtonDeck } from "../../Models/IBuxtonDeck";
import { IMapMarker } from "../../Models/IMapMarker";
import { IRootReducer } from "../../Models/IRootReducer";
import {
    IChartTAAData,
    IFrequentlyVisitedPlace,
    IGCPBigQueryJob,
    IGPSBenchmarkCommonData,
    IGPSData,
    IMobileInsightsMapData,
    IPolygon,
    ISegment,
    IUser
} from "../../Models/Models";
import { polygonsAreIdentical } from "../../Util/MobilyticsUtil";

export const gpsCurrentJobDataSelector = (state: IRootReducer): IGPSData[] =>
    state.mobilyticsReportOutput.data;

export const gpsCurrentJobPolygonSelector = (state: IRootReducer): IPolygon[] =>
    state.mobilyticsReportOutput.polygon;

const gpsCurrentBenchmarkCommonDataSelector = (
    state: IRootReducer
): IGPSBenchmarkCommonData => state.mobilyticsReportOutput.benchmarkCommonData;

const getIsGpsInsightModalVisible = (state: IRootReducer): boolean =>
    state.mobilyticsReportOutput.isGpsInsightModalVisible;

const getGoogleMap = (state: IRootReducer): google.maps.Map<Element> =>
    state.reportMap.googleMap;

const getMapZoom = (state: IRootReducer): number => state.reportMap.mapZoom;

const getCurrentAnchorWindow = (state: IRootReducer): google.maps.InfoWindow =>
    state.reportMap.currentAnchorWindow;

const getBuxtonDeck = (state: IRootReducer): IBuxtonDeck =>
    state.reportMap.buxtonDeck;

const getMapStyleOptions = (state: IRootReducer): MapStyleEnum =>
    state.reportMap.mapStyleOptions;

interface GpsReportMapSelector {
  googleMap: google.maps.Map<Element>;
  mapZoom: number;
  currentAnchorWindow: google.maps.InfoWindow;
  buxtonDeck: IBuxtonDeck;
  mapStyleOptions: MapStyleEnum;
}

export const gpsReportMapSelector = createSelector(
    [
        getGoogleMap,
        getMapZoom,
        getCurrentAnchorWindow,
        getBuxtonDeck,
        getMapStyleOptions,
    ],
    (
        googleMap,
        mapZoom,
        currentAnchorWindow,
        buxtonDeck,
        mapStyleOptions
    ): GpsReportMapSelector => ({
        googleMap,
        mapZoom,
        currentAnchorWindow,
        buxtonDeck,
        mapStyleOptions,
    })
);

export const gpsTAAData = createSelector(
    [gpsCurrentJobDataSelector],
    (gpsData: IGPSData[]): IChartTAAData[][] =>
        gpsData
            .filter((v: IGPSData) => v.tradeAreaAnalysis !== null)
            .map((t) => t.tradeAreaAnalysis)
);

export const gpsConditionalOutputsSelector = createSelector(
    [gpsCurrentJobDataSelector, gpsCurrentJobPolygonSelector],
    (
        gpsData,
        polygon
    ): {
    hasFvpData: boolean;
    hasVisitorLoyaltyData: boolean;
    hasTradeAreaAnalysis: boolean;
  } => {
        const hasFvpData
      = gpsData
      && gpsData.filter((p) => p.frequentlyVisitedPlaces.length > 0).length > 0;
        const hasVisitorLoyaltyData
      = polygon && polygon.length === 2 && !polygonsAreIdentical(polygon);
        const hasTradeAreaAnalysis
      = gpsData
      && gpsData.filter((v: IGPSData) => v.tradeAreaAnalysis !== null).length > 0;

        return {
            hasFvpData,
            hasVisitorLoyaltyData,
            hasTradeAreaAnalysis,
        };
    }
);

export const gpsInsightForReportView = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.selectedGpsInsightForReportView,
    (selectedGpsInsightForReportView: IGCPBigQueryJob[]) =>
        selectedGpsInsightForReportView
);

export const gpsPolygonNames = createSelector(
    [gpsInsightForReportView],
    (selectedGpsInsightForReportView: IGCPBigQueryJob[]) =>
        selectedGpsInsightForReportView.map((j) => j.jobPolygon.name)
);

export const gpsUserSelector = createSelector(
    (state: IRootReducer) => state.user.user,
    (user: IUser) => user
);

export const gpsMosaicSegmentsSelector = createSelector(
    (state: IRootReducer) => state.matchReportOutput.mosaicSegments,
    (segments: ISegment[]) => segments
);

export const gpsLoadingSaveToScoutSelector = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.loadingSaveToScout,
    (loadingSaveToScout: boolean) => loadingSaveToScout
);

export const gpsJobsSelector = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.jobs,
    (jobs: IGCPBigQueryJob[]) => jobs
);

export const gpsLoadingJobsSelector = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.loadingJobs,
    (loading: boolean) => loading
);

export const gpsRunningInsights = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.runningInsights,
    (gpsRunningInsights: string[]) => gpsRunningInsights
);

export const gpsFinishedInsights = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.finishedInsights,
    (gpsFinishedInsights: string[]) => gpsFinishedInsights
);

export const gpsShowVisitorBehaviors = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.showVisitorBehaviorsComponent,
    (showVisitorBehaviorsComponent: boolean) => showVisitorBehaviorsComponent
);

export const gpsCategoriesSelector = createSelector(
    (state: IRootReducer) => state.matchReportOutput.categories,
    (categories: IBrandCategory[]) => categories
);

export const gpsCategoriesLoadingSelector = createSelector(
    (state: IRootReducer) => state.matchReportOutput.loadingCategories,
    (loadingCategories: boolean) => loadingCategories
);

export const gpsFVPMarkersSelector = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.fvpMapMarkers,
    (fvpMapMarkers: IMapMarker[]) => fvpMapMarkers
);

export const gpsFVPSelectedFVPSelector = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.selectedFVP,
    (selectedFVP: IFrequentlyVisitedPlace) => selectedFVP
);

export const gpsPrintReadySelector = createSelector(
    (state: IRootReducer) => state.reportMap.buxtonDeck,
    (buxtonDeck: IBuxtonDeck) => (buxtonDeck ? true : false)
);

export const gpsCurrentBenchmarkIndex = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.selectedGpsInsightJobs,
    (state: IRootReducer) => state.mobilyticsReportOutput.currentBenchmarkJobId,
    (
        selectedGpsInsightJobs: IGCPBigQueryJob[],
        currentBenchmarkJobId: string
    ) => {
        let index = selectedGpsInsightJobs.findIndex(
            (j) => j.jobId === currentBenchmarkJobId
        );
        if (index === -1) {
            index = 0;
        }
        return index;
    }
);

export const gpsCurrentBenchmarkPolygon = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.selectedGpsInsightJobs,
    gpsCurrentBenchmarkIndex,
    (jobs: IGCPBigQueryJob[], currentBenchmarkIndex: number) =>
        jobs && jobs.length > currentBenchmarkIndex
            ? jobs[currentBenchmarkIndex].jobPolygon
            : null
);

export const gpsCurrentBenchmarkPolygonCentroid = createSelector(
    gpsCurrentBenchmarkPolygon,
    (polygon: IPolygon) => {
        return getPolygonCentroid(polygon.linearRings[0]);
    }
);

export const gpsBenchmarkItems = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.selectedGpsInsightJobs,
    (state: IRootReducer) => state.mobilyticsReportOutput.currentBenchmarkJobId,
    (selectedGpsInsightJobs: IGCPBigQueryJob[], currentBenchmarkJobId: string) =>
        selectedGpsInsightJobs.map((j) => ({
            jobId: j.jobId,
            name: j.jobPolygon.name,
            isSelected: j.jobId === currentBenchmarkJobId,
        }))
);

export const filterStoreData = (
    data: IMobileInsightsMapData[],
    isHome: boolean
) => {
    if (data) {
        if (isHome) {
            return data.filter((point) => point.homeLongitude !== 0);
        } else {
            return data.filter((point) => point.workLongitude !== 0);
        }
    } else {
        return data;
    }
};

export const getGpsReportDataSelector = createSelector(
    [
        gpsCurrentJobDataSelector,
        gpsInsightForReportView,
        gpsCurrentBenchmarkCommonDataSelector,
        getIsGpsInsightModalVisible,
        gpsCurrentJobPolygonSelector,
        gpsCurrentBenchmarkPolygon,
        gpsBenchmarkItems,
    ],
    (
        currentJobData,
        selectedInsight,
        benchmarkCommonData,
        isGpsInsightModalVisible,
        polygons,
        currentBenchmarkPolygon,
        benchmarkItems
    ) => ({
        currentJobData,
        selectedInsight,
        benchmarkCommonData,
        isGpsInsightModalVisible,
        polygons,
        currentBenchmarkPolygon,
        benchmarkItems,
    })
);

interface FrequentlyVisitedPlacesStore {
  jobs: IGCPBigQueryJob[];
  loadingJobs: boolean;
  categories: IBrandCategory[];
  loadingCategories: boolean;
  mapMarkers: IMapMarker[];
  selectedFVP: IFrequentlyVisitedPlace | null;
}

export const gpsFVPDataSelector = createSelector(
    [
        gpsInsightForReportView,
        gpsLoadingJobsSelector,
        gpsCategoriesSelector,
        gpsCategoriesLoadingSelector,
        gpsFVPMarkersSelector,
        gpsFVPSelectedFVPSelector,
    ],
    (
        jobs,
        loadingJobs,
        categories,
        loadingCategories,
        mapMarkers,
        selectedFVP
    ): FrequentlyVisitedPlacesStore => ({
        jobs,
        loadingJobs,
        categories,
        loadingCategories,
        mapMarkers,
        selectedFVP,
    })
);

interface VisitorBehaviorsSelector {
  fvpData: IFrequentlyVisitedPlace[][];
  showVisitorBehaviors: boolean;
  polygons: IPolygon[];
}

export const gpsVisitorBehaviorsContainerSelector = createSelector(
    [
        gpsCurrentJobDataSelector,
        gpsShowVisitorBehaviors,
        gpsCurrentJobPolygonSelector,
    ],
    (data, showVisitorBehaviors, polygons): VisitorBehaviorsSelector => ({
        fvpData: data
            .filter((p: IGPSData) => p.frequentlyVisitedPlaces.length > 0)
            .map((t) => t.frequentlyVisitedPlaces),
        showVisitorBehaviors,
        polygons,
    })
);

export const loadingMobilyticsReportSelector = createSelector(
    (state: IRootReducer) => state.mobilyticsReportOutput.loadingReport,
    (loadingReport: boolean) => loadingReport
);